import React, { useMemo, useRef, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FileUp, Loader2, FileText, CheckCircle2, AlertTriangle, Clock, X, CheckCheck } from "lucide-react";
import { externalReportsApi, buildingsApi } from "@/services/api";
import { useAsync } from "@/hooks/useAsync";
import { fmtDateTime, apiError } from "@/lib/format";
import PageHeader from "@/components/PageHeader";
import UpgradeBanner from "@/components/UpgradeBanner";
import { PrivacyNotice } from "@/components/PrivacyNotice";
import { LoadingState, ErrorState, EmptyState } from "@/components/DataStates";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

const ACCEPT = ".pdf,.docx,.xlsx,.xls,.jpg,.jpeg,.png,.webp,.heic";

function statusBadge(r) {
  const ps = r.processing_status || "ready";
  if (ps === "processing") return <Badge variant="outline" className="bg-amber-100 text-amber-800 border-amber-200"><Clock className="mr-1 h-3 w-3" /> Extracting…</Badge>;
  if (ps === "failed") return <Badge variant="outline" className="bg-red-100 text-red-800 border-red-200"><AlertTriangle className="mr-1 h-3 w-3" /> Failed</Badge>;
  if (r.status === "promoted") return <Badge variant="outline" className="bg-emerald-100 text-emerald-800 border-emerald-200"><CheckCircle2 className="mr-1 h-3 w-3" /> Promoted</Badge>;
  const n = (r.extracted_data?.doors || []).length;
  return <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">{n} door{n === 1 ? "" : "s"} extracted</Badge>;
}

function UploadDialog({ open, onOpenChange, onUploaded }) {
  const buildings = useAsync(() => (open ? buildingsApi.getAll() : Promise.resolve({ data: [] })), [open]);
  const [files, setFiles] = useState([]);
  const [buildingId, setBuildingId] = useState("");
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const inputRef = useRef(null);

  useEffect(() => { if (!open) { setFiles([]); setBuildingId(""); setProgress({ done: 0, total: 0 }); } }, [open]);

  const addFiles = (fileList) => {
    const picked = Array.from(fileList || []);
    if (!picked.length) return;
    setFiles((prev) => {
      const seen = new Set(prev.map((f) => `${f.name}:${f.size}`));
      return [...prev, ...picked.filter((f) => !seen.has(`${f.name}:${f.size}`))];
    });
  };
  const removeFile = (i) => setFiles((prev) => prev.filter((_, idx) => idx !== i));

  const submit = async () => {
    if (!files.length) { toast.error("Choose one or more report files to upload."); return; }
    setUploading(true);
    setProgress({ done: 0, total: files.length });
    let ok = 0;
    const failed = [];
    for (let i = 0; i < files.length; i++) {
      const f = files[i];
      try {
        const fd = new FormData();
        fd.append("file", f);
        if (buildingId) fd.append("building_id", buildingId);
        await externalReportsApi.upload(fd);
        ok += 1;
      } catch (e) {
        failed.push(f.name);
      }
      setProgress({ done: i + 1, total: files.length });
    }
    setUploading(false);
    if (ok) toast.success(`Uploaded ${ok} report${ok === 1 ? "" : "s"} — extracting doors with AI…`);
    if (failed.length) toast.error(`${failed.length} file${failed.length === 1 ? "" : "s"} failed: ${failed.slice(0, 3).join(", ")}${failed.length > 3 ? "…" : ""}`);
    if (ok) onUploaded();
  };

  const bldList = Array.isArray(buildings.data) ? buildings.data : [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Import external reports</DialogTitle>
          <DialogDescription>Upload one or more supplier inspection reports (PDF, Word, Excel or image) — e.g. a separate PDF per door. AI extracts the doors and defects from each.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <PrivacyNotice testId="ai-import-notice">
            Uploaded documents are processed by an automated AI service (Google Gemini) to extract door and defect data. Nothing is saved to your register until you review and promote it. Please don&apos;t upload documents containing special-category personal data (e.g. health or ID documents).
          </PrivacyNotice>
          <div className="space-y-2">
            <Label>Report files *</Label>
            <input ref={inputRef} type="file" multiple accept={ACCEPT} className="hidden" onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} data-testid="report-file-input" />
            <Button variant="outline" className="w-full justify-start" onClick={() => inputRef.current?.click()} data-testid="report-choose-file">
              <FileUp className="mr-2 h-4 w-4" /> {files.length ? `Add more… (${files.length} selected)` : "Choose files…"}
            </Button>
            {files.length > 0 && (
              <div className="max-h-44 space-y-1.5 overflow-y-auto rounded-md border border-slate-200 p-2" data-testid="report-file-list">
                {files.map((f, i) => (
                  <div key={`${f.name}-${i}`} className="flex items-center justify-between gap-2 rounded bg-slate-50 px-2 py-1.5 text-sm" data-testid={`report-file-${i}`}>
                    <span className="flex min-w-0 items-center gap-2"><FileText className="h-4 w-4 shrink-0 text-slate-400" /><span className="truncate text-slate-700">{f.name}</span></span>
                    {!uploading && (
                      <button type="button" onClick={() => removeFile(i)} className="shrink-0 text-slate-400 hover:text-red-500" data-testid={`report-file-remove-${i}`} aria-label={`Remove ${f.name}`}>
                        <X className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="space-y-2">
            <Label>Building (optional — AI will try to match)</Label>
            <Select value={buildingId || "auto"} onValueChange={(v) => setBuildingId(v === "auto" ? "" : v)}>
              <SelectTrigger data-testid="report-building-select"><SelectValue placeholder="Auto-match" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="auto">Auto-match from report</SelectItem>
                {bldList.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
              </SelectContent>
            </Select>
            {files.length > 1 && buildingId && <p className="text-xs text-slate-500">All {files.length} reports will be linked to this building.</p>}
          </div>
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={uploading || !files.length} data-testid="report-upload-btn">
            {uploading
              ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Uploading {progress.done}/{progress.total}…</>
              : <><FileUp className="mr-2 h-4 w-4" /> Upload &amp; extract{files.length > 1 ? ` (${files.length})` : ""}</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function ExternalReports() {
  const navigate = useNavigate();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [promoteAllOpen, setPromoteAllOpen] = useState(false);
  const { data, loading, error, reload } = useAsync(() => externalReportsApi.list(), []);
  const list = useMemo(() => (Array.isArray(data) ? data : []), [data]);

  // Reports that are done extracting, have doors, and aren't promoted yet.
  const promotable = useMemo(
    () => list.filter((r) =>
      (r.processing_status || "ready") === "ready" &&
      r.status !== "promoted" &&
      ((r.extracted_data?.doors || []).length > 0)),
    [list],
  );
  const promotableDoors = useMemo(
    () => promotable.reduce((s, r) => s + (r.extracted_data?.doors || []).length, 0),
    [promotable],
  );

  // Poll while any report is still processing.
  const anyProcessing = list.some((r) => (r.processing_status || "ready") === "processing");
  useEffect(() => {
    if (!anyProcessing) return;
    const t = setInterval(() => reload(), 5000);
    return () => clearInterval(t);
  }, [anyProcessing, reload]);

  return (
    <div>
      <PageHeader
        title="Report imports"
        subtitle="Bring third-party inspection reports into FD Check"
        testId="imports-header"
        actions={
          <div className="flex items-center gap-2">
            {promotable.length > 0 && (
              <Button variant="outline" onClick={() => setPromoteAllOpen(true)} data-testid="imports-promote-all-btn">
                <CheckCheck className="mr-1.5 h-4 w-4" /> Promote all ({promotableDoors})
              </Button>
            )}
            <Button onClick={() => setDialogOpen(true)} data-testid="imports-new-btn"><FileUp className="mr-1.5 h-4 w-4" /> Import reports</Button>
          </div>
        }
      />

      <UpgradeBanner feature="external_reports" className="mb-4" title="AI report import is a Pro feature">Upload third-party inspection reports and let AI extract the doors and defects.</UpgradeBanner>

      {loading && <LoadingState label="Loading imports…" />}
      {!loading && error && <ErrorState message={error} onRetry={reload} />}

      {!loading && !error && (
        list.length === 0 ? (
          <EmptyState title="No imported reports" message="Upload one or more supplier PDFs (e.g. a PDF per door) and AI will extract the doors and defects, ready to promote into your register or quote." testId="imports-empty" action={<Button onClick={() => setDialogOpen(true)}><FileUp className="mr-1.5 h-4 w-4" /> Import reports</Button>} />
        ) : (
          <div className="space-y-2" data-testid="imports-list">
            {list.map((r) => (
              <Card key={r.id} className="flex cursor-pointer items-center justify-between gap-3 p-4 transition-shadow hover:shadow-md" onClick={() => navigate(`/imports/${r.id}`)} data-testid={`import-row-${r.id}`}>
                <div className="flex min-w-0 items-center gap-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary"><FileText className="h-5 w-5" /></div>
                  <div className="min-w-0">
                    <p className="truncate font-medium text-slate-800">{r.filename || "Report"}</p>
                    <p className="truncate text-sm text-slate-500">{fmtDateTime(r.created_at)}</p>
                  </div>
                </div>
                {statusBadge(r)}
              </Card>
            ))}
          </div>
        )
      )}

      <UploadDialog open={dialogOpen} onOpenChange={setDialogOpen} onUploaded={() => { setDialogOpen(false); reload(); }} />
      <PromoteAllDialog
        open={promoteAllOpen}
        onOpenChange={setPromoteAllOpen}
        reports={promotable}
        onDone={() => { setPromoteAllOpen(false); reload(); }}
      />
    </div>
  );
}

function PromoteAllDialog({ open, onOpenChange, reports, onDone }) {
  const buildings = useAsync(() => (open ? buildingsApi.getAll() : Promise.resolve({ data: [] })), [open]);
  const [buildingId, setBuildingId] = useState("");
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });

  useEffect(() => { if (!open) { setBuildingId(""); setProgress({ done: 0, total: 0 }); } }, [open]);

  const totalDoors = reports.reduce((s, r) => s + (r.extracted_data?.doors || []).length, 0);
  const noBuilding = reports.filter((r) => !r.building_id).length;
  const bldList = Array.isArray(buildings.data) ? buildings.data : [];

  const run = async () => {
    setBusy(true);
    setProgress({ done: 0, total: reports.length });
    let promoted = 0;
    let doors = 0;
    let merged = 0;
    let skipped = 0;
    const failed = [];
    for (let i = 0; i < reports.length; i++) {
      const r = reports[i];
      try {
        const body = { create_inspections: true };
        if (buildingId) body.building_id = buildingId;
        const res = await externalReportsApi.promote(r.id, body);
        promoted += 1;
        doors += res?.data?.doors_created ?? (r.extracted_data?.doors || []).length;
        merged += res?.data?.doors_reused || 0;
      } catch (e) {
        const code = e?.response?.status;
        if (code === 409 || code === 400) skipped += 1;
        else failed.push(r.filename || "Report");
      }
      setProgress({ done: i + 1, total: reports.length });
    }
    setBusy(false);
    if (promoted) toast.success(`Promoted ${promoted} report${promoted === 1 ? "" : "s"} (${doors} new door${doors === 1 ? "" : "s"}) into your register.`);
    if (merged) toast.message(`${merged} duplicate door${merged === 1 ? "" : "s"} merged into existing register entries.`);
    if (skipped) toast.message(`${skipped} skipped — already promoted or no building linked.`);
    if (failed.length) toast.error(`${failed.length} failed: ${failed.slice(0, 3).join(", ")}${failed.length > 3 ? "…" : ""}`);
    onDone();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Promote all extracted doors</DialogTitle>
          <DialogDescription>
            Create native doors {"&"} inspections for {totalDoors} door{totalDoors === 1 ? "" : "s"} across {reports.length} report{reports.length === 1 ? "" : "s"}. Already-promoted reports are skipped.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Target building</Label>
            <Select value={buildingId || "auto"} onValueChange={(v) => setBuildingId(v === "auto" ? "" : v)}>
              <SelectTrigger data-testid="promote-all-building-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="auto">Use each report&apos;s linked building</SelectItem>
                {bldList.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
              </SelectContent>
            </Select>
            {!buildingId && noBuilding > 0 && (
              <p className="text-xs text-amber-600" data-testid="promote-all-nobuilding-warn">
                {noBuilding} report{noBuilding === 1 ? " isn't" : "s aren't"} linked to a building and will be skipped. Pick a building above to include {noBuilding === 1 ? "it" : "them"}.
              </p>
            )}
            {buildingId && (
              <p className="text-xs text-slate-500">All {reports.length} report{reports.length === 1 ? "" : "s"} will be linked to this building.</p>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
          <Button onClick={run} disabled={busy} data-testid="promote-all-confirm-btn">
            {busy
              ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Promoting {progress.done}/{progress.total}…</>
              : <><CheckCheck className="mr-2 h-4 w-4" /> Promote all</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
